import React, { useEffect, useState } from "react";
import MultiSelect from "../../components/MultiSelect";
import { Option } from "../../interfaces/MultiSelectTypes";
import { Visit, VisitService } from "../../interfaces/Visit";
import { updateField } from "../../utils/StateUtil";
import { useAppDispatch } from "../../hooks";
import { getServices } from "../../apis/service";
type ServiceSelectProps = {
  visit: Visit;
  onCreate: (value: string) => void;
  onServiceChange: (services: Array<VisitService>) => void;
};
const ServiceSelect: React.FC<ServiceSelectProps> = ({
  visit,
  onCreate,
  onServiceChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [services, setServices] = useState<Array<VisitService>>([]);

  const dispatch = useAppDispatch();

  useEffect(() => {
    fetchServices();
  }, []);

  // For OPD visits, default selected service to consultancy when none selected.
  // Consultancy may be on a later page, so fetch by name if not in current options.
  useEffect(() => {
    const isOpd = visit.clinicVisitType.visitType.type === "opd";
    if (!isOpd || visit.services.length > 0) return;

    const consultancyInList = services.find(
      (s) => s.name.toLowerCase().trim() === "consultancy"
    );

    if (consultancyInList) {
      const defaultServices = [
        {
          id: consultancyInList.id,
          name: consultancyInList.name,
          fee: consultancyInList.fee,
          quantity: 1,
        },
      ];
      updateField("services", defaultServices, dispatch);
      onServiceChange(defaultServices);
      return;
    }

    // Consultancy not on first page — fetch it by name so we can set default and show in options
    const fetchConsultancyAndSetDefault = async () => {
      try {
        const {
          data: { data: response },
        } = await getServices({ name: "consultancy" });
        const consultancy = Array.isArray(response)
          ? response.find((s: VisitService) => s.name.toLowerCase().trim() === "consultancy")
          : null;
        if (!consultancy) return;
        const defaultServices = [
          {
            id: consultancy.id,
            name: consultancy.name,
            fee: consultancy.fee,
            quantity: 1,
          },
        ];
        setServices((prev) => {
          const hasConsultancy = prev.some(
            (s) => s.name.toLowerCase().trim() === "consultancy"
          );
          return hasConsultancy ? prev : [consultancy, ...prev];
        });
        updateField("services", defaultServices, dispatch);
        onServiceChange(defaultServices);
      } catch (error) {
        console.log(error);
      }
    };
    fetchConsultancyAndSetDefault();
  }, [services, visit.clinicVisitType.visitType.type, visit.services.length]);

  const handleSearch = (search: string) => {
    setSearchTerm(search);
    fetchServices(search);
  };

  const fetchServices = async (search: string = "") => {
    try {
      const {
        data: { data: response },
      } = await getServices({ name: search });
      setServices(response);
    } catch (error) {
      console.log(error);
    }
  };

  const toggleDropdown = () => setIsOpen(!isOpen);

  const handleServiceSelect = (option: Option) => {
    if (
      !visit.services.some((selected) => {
        return selected.id.toString() === option.value;
      })
    ) {
      const [service] = services.filter(
        (service) => service.id.toString() === option.value
      );
      const updatedServices = [
        ...visit.services,
        {
          id: service.id,
          name: service.name,
          fee: service.fee,
          quantity: 1,
        },
      ];
      updateField("services", updatedServices, dispatch);
      onServiceChange(updatedServices);
    } else {
      console.log(
        "handle this else condition ask sanket either add quantity or remove item?"
      );
    }
  };

  const handleServiceRemoval = (option: Option) => {
    const updatedServices = visit.services.filter(
      (selected) => selected.id.toString() !== option.value
    );
    updateField("services", updatedServices, dispatch);
    onServiceChange(updatedServices);
  };

  return (
    <MultiSelect
      onAddNewOption={() => onCreate(searchTerm)}
      selectedOptions={visit.services.map((i) => ({
        label: i.name,
        value: i.id.toString(),
      }))}
      options={services.map((i) => ({ label: i.name, value: i.id.toString() }))}
      isOpen={isOpen}
      searchTerm={searchTerm}
      onSearch={handleSearch}
      onOptionSelect={handleServiceSelect}
      onOptionRemove={handleServiceRemoval}
      toggleDropdown={toggleDropdown}
    />
  );
};

export default ServiceSelect;
